/**
 * Analytics Feature
 *
 * Provides PostHog Analytics integration with type-safe event logging
 * for Web, iOS, and Android platforms. Includes user identification,
 * group analytics, and screen tracking.
 * @module features/analytics
 * @example
 * ```typescript
 * import { AnalyticsTracker } from "@/features/analytics";
 *
 * // Log a screen view (core event)
 * AnalyticsTracker.logScreenView({
 *   screen_name: "Home",
 *   screen_class: "HomeScreen"
 * });
 *
 * // Identify user after sign-in
 * AnalyticsTracker.identifyUser("user-123", { phone_number: "+1234567890" });
 *
 * // Associate user with organization
 * AnalyticsTracker.setGroup("organization", "org-456", { name: "Acme FC" });
 *
 * // Reset identity on sign-out
 * AnalyticsTracker.resetIdentity();
 * ```
 */

// =============================================================================
// Main Tracker Export
// =============================================================================

export { AnalyticsTracker } from "./utils/analyticsTracker";

export { getAnalyticsEnvironment } from "./utils/posthogSender";

// =============================================================================
// Hook Export
// =============================================================================

export { useAnalytics } from "./hooks/useAnalytics";

// =============================================================================
// Type Exports
// =============================================================================

export type {
  // Event Parameter Types
  TLogScreenViewParams,
  TPageNotFoundParams,
  TNoteCreatedParams,
  TNoteDeletedParams,
  TUserSignInParams,
  TUserSignOutParams,
  TOrganizationJoinedParams,
  TOrganizationLeftParams,
  TPlayerWatchlistParams,
  TWatchlistManagementParams,
  TProfilerRenderParams,
  TPlayerProfileViewedParams,
  TPlayerComparisonViewedParams,
  TSimilarPlayersViewedParams,
  TSearchPerformedParams,
  TFilterAppliedParams,
  TKanbanBoardViewedParams,
  TMatchIntakeStartedParams,
  TReportOpenedParams,
  TShadowTeamViewedParams,
  TKnowledgeGraphOpenedParams,
  THomeViewedParams,
  TActivityFeedViewedParams,
  TWatchlistViewedParams,
  TRosterViewedParams,
  TRecommendationsViewedParams,
  TPipelineCreatedParams,
  TPipelineCreationSource,
  TPipelineStageEditedParams,
  TPipelineStageEditAction,
  TCardAddedParams,
  TCardAddedSource,
  TCardMovedParams,
  TCardMoveMethod,
  TCardRemovedParams,
  TSuggestionActedParams,
  TRuleCreatedParams,
  TReviewItemResolvedParams,
  TViewToggledParams,
  TPipelineLoadTimeParams,
  TTierBlockRenderParams,
  TTierBlockSurface,
  TNotificationTappedParams,
  TNotificationPreferenceUpdatedParams,
  TOrganizationCreatedParams,
  TOrganizationUpdatedParams,
  TUserInvitedParams,
  // Identity Types
  TIdentifyUserParams,
  TGroupParams,
  // Validation Types
  TEventParameter,
  TEventParameterValue,
  IValidateEvent,
  // Service Interfaces
  ICoreAnalyticsService,
  INoteAnalyticsService,
  IUserAnalyticsService,
  IProfilerAnalyticsService,
  IPlayerAnalyticsService,
  ISearchAnalyticsService,
  IPipelineAnalyticsService,
  IScoutingAnalyticsService,
  ISurfaceAnalyticsService,
  INotificationAnalyticsService,
  IIdentityAnalyticsService,
} from "./types";

// =============================================================================
// Constant Exports
// =============================================================================

export {
  ANALYTICS_EVENTS,
  CORE_EVENTS,
  USER_EVENTS,
  NOTE_EVENTS,
  PROFILER_EVENTS,
} from "./constants";
